import React, { useEffect, useState } from "react";
import {
  Headset, Timer as TimerIcon, X, Lightbulb, CaretRight,
  IdentificationCard, PaperPlaneTilt, Clock, Trophy, GlobeHemisphereWest,
} from "@phosphor-icons/react";
import {
  PITCH_STEPS, OBJECTIONS, DCF,
  FOUND_VIA_OPTIONS, HAS_WEBSITE_OPTIONS, INTEREST_OPTIONS,
} from "./callListConstants";
import { formatElapsed } from "./callListHelpers";
import EmailPreviewModal from "./EmailPreviewModal";
import { MicButton } from "./MicButton";
import PersonalizedPitchCard from "./PersonalizedPitchCard";
import ObjectionCoachPanel from "./ObjectionCoachPanel";

/**
 * Inline "Mode appel" panel expanded below a business row.
 * Includes live call timer, 4-step pitch progress, objection chips,
 * contact info capture and pre-written email compose.
 */
export default function AssistPanel({ biz, telepro, onSetStatus, onQualify, onSaveNote, onSaveContact, onClose }) {
  const [stepIdx, setStepIdx] = useState(0);
  const [openObjection, setOpenObjection] = useState(null);
  const [elapsed, setElapsed] = useState(0);
  const [noteDraft, setNoteDraft] = useState(biz.call.note || "");
  const [contactName, setContactName] = useState(biz.call.contact_name || "");
  const [contactPhone, setContactPhone] = useState(biz.call.contact_phone || "");
  const [contactEmail, setContactEmail] = useState(biz.call.contact_email || "");
  const [emailPreviewOpen, setEmailPreviewOpen] = useState(false);

  // Structured Q1/Q2/Q3 answers — checkbox/radio state, auto-saved to backend.
  const initialAnswers = biz.call.prospect_answers || {};
  const [foundVia, setFoundVia] = useState(new Set(initialAnswers.found_via || []));
  const [hasWebsite, setHasWebsite] = useState(initialAnswers.has_website || null);
  const [interest, setInterest] = useState(initialAnswers.interest || null);

  const toggleFoundVia = (id) => {
    const next = new Set(foundVia);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setFoundVia(next);
    onSaveContact({ prospect_found_via: Array.from(next) });
  };
  const chooseHasWebsite = (v) => {
    setHasWebsite(v);
    onSaveContact({ prospect_has_website: v });
  };
  const chooseInterest = (v) => {
    setInterest(v);
    onSaveContact({ prospect_interest: v });
  };

  useEffect(() => {
    const t = setInterval(() => setElapsed((v) => v + 1), 1000);
    return () => clearInterval(t);
  }, []);

  // (Chiffres live à citer — géré désormais par LiveStatsBanner en tête de la liste)

  const step = PITCH_STEPS[stepIdx];
  const progressPct = Math.round(((stepIdx + 1) / PITCH_STEPS.length) * 100);
  const currentMethod = biz.call.contact_method || null;

  const closeAfter = (cb) => () => { cb(); onClose && onClose(); };

  const persistContact = (field, value) => {
    if (value === (biz.call[field] || "")) return;
    onSaveContact({ [field]: value });
  };

  const setMethod = (m) => {
    onSaveContact({ contact_method: m === currentMethod ? null : m });
  };

  return (
    <div
      className="border-t border-primary/30 bg-gradient-to-br from-primary/5 via-card to-card px-3 py-4 sm:px-5 sm:py-5"
      data-testid={`assist-panel-${biz.id}`}
    >
      <div className="flex items-center justify-between gap-3 mb-3">
        <div className="flex items-center gap-2">
          <span className="w-7 h-7 grid place-items-center rounded-sm bg-primary/15 text-primary">
            <Headset size={14} weight="duotone"/>
          </span>
          <div>
            <div className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground">Mode appel</div>
            <div className="font-display text-sm font-semibold truncate max-w-[200px] sm:max-w-none">{biz.name}</div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <span className="inline-flex items-center gap-1.5 bg-primary/10 text-primary border border-primary/40 rounded-sm px-2 py-1 text-xs font-mono tabular-nums" data-testid={`assist-timer-${biz.id}`}>
            <TimerIcon size={12} weight="duotone"/> {formatElapsed(elapsed)}
          </span>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground p-1" aria-label="Fermer" data-testid={`assist-close-${biz.id}`}>
            <X size={14}/>
          </button>
        </div>
      </div>

      {/* Le bandeau "🔴 Chiffres live à citer" est déjà affiché en tête de
          TeleproCallList (LiveStatsBanner) — pas de doublon ici. */}

      {/* Vague 11-B — Pitch personnalisé IA */}
      <PersonalizedPitchCard biz={biz}/>

      {/* VAGUE 42 — Coach commercial en direct */}
      <ObjectionCoachPanel biz={biz} onQualify={onQualify} onStatus={onSetStatus}/>

      <div className="mb-3" data-testid={`assist-steps-${biz.id}`}>
        <div className="flex items-center justify-between gap-1 mb-2">
          {PITCH_STEPS.map((s, i) => {
            const active = i === stepIdx;
            const done = i < stepIdx;
            return (
              <button
                key={s.id}
                onClick={() => setStepIdx(i)}
                className={`flex-1 text-left px-2 py-1.5 rounded-sm text-[11px] font-bold border transition ${
                  active ? "bg-primary text-primary-foreground border-primary"
                  : done ? "bg-primary/10 text-primary border-primary/30"
                  : "bg-card text-muted-foreground border-border hover:border-primary/40"
                }`}
                data-testid={`assist-step-${biz.id}-${s.id}`}
              >
                <span className="block text-[9px] uppercase tracking-wider opacity-80">Étape {i + 1}</span>
                <span className="block leading-tight">{s.label}</span>
              </button>
            );
          })}
        </div>
        <div className="h-1 bg-muted rounded-full overflow-hidden">
          <div className={`h-full ${step.color} transition-all duration-500`} style={{ width: `${progressPct}%` }}/>
        </div>
      </div>

      <div className="bg-card border border-border rounded-sm p-3 mb-3" data-testid={`assist-step-body-${biz.id}`}>
        <div className="text-[10px] uppercase tracking-widest font-bold text-primary mb-1.5 flex items-center gap-1">
          <Lightbulb size={11} weight="duotone"/> Dis maintenant
        </div>
        <p className="text-sm leading-relaxed whitespace-pre-line text-foreground/90">{step.body}</p>
        <div className="flex justify-between gap-2 mt-3">
          <button
            onClick={() => setStepIdx((v) => Math.max(0, v - 1))}
            disabled={stepIdx === 0}
            className="text-xs text-muted-foreground hover:text-foreground disabled:opacity-30"
            data-testid={`assist-prev-${biz.id}`}
          >
            ← Étape précédente
          </button>
          <button
            onClick={() => setStepIdx((v) => Math.min(PITCH_STEPS.length - 1, v + 1))}
            disabled={stepIdx === PITCH_STEPS.length - 1}
            className="text-xs font-semibold text-primary hover:underline disabled:opacity-30 inline-flex items-center gap-1"
            data-testid={`assist-next-${biz.id}`}
          >
            Étape suivante <CaretRight size={10} weight="bold"/>
          </button>
        </div>
      </div>

      {/* Fiche prospect — cases à cocher / radios pour capturer Q1/Q2/Q3.
          Apparaît toujours (utile même hors étape Découverte : le télépro peut
          cocher au fil du call). Auto-save via onSaveContact. */}
      <div className="bg-card border border-border rounded-sm p-3 mb-3" data-testid={`assist-prospect-form-${biz.id}`}>
        <div className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground mb-2">
          🎯 Fiche prospect — coche au fil du call
        </div>

        <div className="mb-3">
          <div className="text-xs font-semibold text-foreground/90 mb-1.5">Q1. Comment leurs clients les trouvent aujourd&apos;hui ?</div>
          <div className="flex flex-wrap gap-1.5">
            {FOUND_VIA_OPTIONS.map((o) => {
              const checked = foundVia.has(o.id);
              return (
                <button
                  key={o.id}
                  type="button"
                  onClick={() => toggleFoundVia(o.id)}
                  className={`text-[11px] font-semibold border rounded-sm px-2.5 py-1 transition ${
                    checked
                      ? "bg-primary text-primary-foreground border-primary hover:bg-[#0f3d29] hover:text-white"
                      : "bg-card hover:bg-primary/10 text-foreground border-border"
                  }`}
                  data-testid={`q1-found-via-${biz.id}-${o.id}`}
                >
                  {checked ? "✓ " : ""}{o.label}
                </button>
              );
            })}
          </div>
        </div>

        <div className="mb-3">
          <div className="text-xs font-semibold text-foreground/90 mb-1.5">Q2. Ont-ils un site internet ?</div>
          <div className="flex flex-wrap gap-1.5">
            {HAS_WEBSITE_OPTIONS.map((o) => {
              const active = hasWebsite === o.id;
              return (
                <button
                  key={o.id}
                  type="button"
                  onClick={() => chooseHasWebsite(o.id)}
                  className={`text-[11px] font-semibold border rounded-sm px-2.5 py-1 transition ${
                    active
                      ? "bg-primary text-primary-foreground border-primary hover:bg-[#0f3d29] hover:text-white"
                      : "bg-card hover:bg-primary/10 text-foreground border-border"
                  }`}
                  data-testid={`q2-has-website-${biz.id}-${o.id}`}
                >
                  {active ? "● " : "○ "}{o.label}
                </button>
              );
            })}
          </div>

          {/* Upsell Digital Corp France — apparaît quand le prospect n'a pas
              de site web. C'est LE moment stratégique pour proposer un site
              par Damien / Digital Corp France. */}
          {hasWebsite === "no" && (
            <div
              className="mt-3 bg-[#1a5f8a]/8 border-l-4 border-[#1a5f8a] rounded-sm p-3"
              data-testid={`dcf-upsell-${biz.id}`}
            >
              <div className="flex items-center gap-1.5 text-[#1a5f8a] font-bold text-xs uppercase tracking-wider mb-1.5">
                <GlobeHemisphereWest size={13} weight="fill"/> Opportunité : proposer un site web
              </div>
              <p className="text-xs text-foreground/90 leading-relaxed mb-2">
                <b>Pitch à faire maintenant :</b> « J&apos;ai remarqué que vous n&apos;avez pas encore de site web.
                Sachez que notre partenaire <b>Digital Corp France</b> — la même équipe qui gère MonCommerce68 —
                crée des sites vitrine pros à des tarifs adaptés aux commerces locaux. Je peux vous mettre
                en relation avec <b>{DCF.contact}</b> directement, il vous fera un devis gratuit sans engagement. »
              </p>
              <div className="text-[11px] bg-white/60 border border-[#1a5f8a]/20 rounded-sm p-2 space-y-0.5 mb-2 font-mono" data-testid={`dcf-coords-${biz.id}`}>
                <div><b>Contact :</b> {DCF.contact}</div>
                <div><b>Tél :</b> <a href={`tel:${DCF.phone.replace(/\s/g,"")}`} className="text-[#1a5f8a] hover:underline">{DCF.phone}</a></div>
                <div><b>Email :</b> <a href={`mailto:${DCF.email}`} className="text-[#1a5f8a] hover:underline">{DCF.email}</a></div>
                <div><b>Site :</b> <a href={DCF.website_url} target="_blank" rel="noopener noreferrer" className="text-[#1a5f8a] hover:underline">{DCF.website}</a></div>
              </div>
              <div className="text-[10px] text-muted-foreground italic">
                Si le prospect est chaud pour un site → clique <b>« Besoin site web »</b> dans les actions
                ci-dessous. Damien recevra la fiche prospect en priorité.
              </div>
            </div>
          )}
        </div>

        <div>
          <div className="text-xs font-semibold text-foreground/90 mb-1.5">Q3. Niveau d&apos;intérêt pour le Premium -50 % ?</div>
          <div className="flex flex-wrap gap-1.5">
            {INTEREST_OPTIONS.map((o) => {
              const active = interest === o.id;
              return (
                <button
                  key={o.id}
                  type="button"
                  onClick={() => chooseInterest(o.id)}
                  className={`text-[11px] font-semibold border rounded-sm px-2.5 py-1 transition ${
                    active
                      ? "bg-primary text-primary-foreground border-primary hover:bg-[#0f3d29] hover:text-white"
                      : "bg-card hover:bg-primary/10 text-foreground border-border"
                  }`}
                  data-testid={`q3-interest-${biz.id}-${o.id}`}
                >
                  {active ? "● " : "○ "}{o.label}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      <div className="mb-3" data-testid={`assist-objections-${biz.id}`}>
        <div className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground mb-1.5">
          Objections — clique pour la réponse
        </div>
        <div className="flex flex-wrap gap-1.5">
          {OBJECTIONS.map((o) => {
            const open = openObjection === o.id;
            return (
              <button
                key={o.id}
                onClick={() => setOpenObjection(open ? null : o.id)}
                className={`text-[11px] font-semibold border rounded-sm px-2 py-1 transition ${
                  open ? "bg-secondary text-secondary-foreground border-secondary"
                       : "bg-card hover:bg-secondary/10 text-foreground border-border"
                }`}
                data-testid={`assist-objection-btn-${biz.id}-${o.id}`}
              >
                {o.label}
              </button>
            );
          })}
        </div>
        {openObjection && (
          <div className="mt-2 bg-secondary/10 border border-secondary/30 rounded-sm p-2.5 text-xs leading-relaxed text-foreground/90" data-testid={`assist-objection-reply-${biz.id}`}>
            <b className="text-secondary">Réponse :</b> {OBJECTIONS.find((o) => o.id === openObjection)?.reply}
          </div>
        )}
      </div>

      <div className="mb-3" data-testid={`assist-contact-${biz.id}`}>
        <div className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground mb-1.5 flex items-center gap-1">
          <IdentificationCard size={11} weight="duotone"/> Coordonnées du prospect — auto-enregistrées
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-1.5">
          <label className="block">
            <span className="text-[10px] text-muted-foreground block mb-0.5">Décideur</span>
            <input
              value={contactName}
              onChange={(e) => setContactName(e.target.value.slice(0, 120))}
              onBlur={() => persistContact("contact_name", contactName.trim())}
              placeholder="Mr. Müller"
              className="w-full text-xs border border-border rounded-sm px-2 py-1.5 bg-background focus:border-primary outline-none"
              data-testid={`contact-name-input-${biz.id}`}
            />
          </label>
          <label className="block">
            <span className="text-[10px] text-muted-foreground block mb-0.5">Téléphone</span>
            <input
              type="tel"
              inputMode="tel"
              autoComplete="tel"
              value={contactPhone}
              onChange={(e) => setContactPhone(e.target.value.slice(0, 40))}
              onBlur={() => persistContact("contact_phone", contactPhone.trim())}
              placeholder="06 12 34 56 78"
              className="w-full text-base sm:text-xs font-mono border border-border rounded-sm px-2 py-2 sm:py-1.5 bg-background focus:border-primary outline-none min-h-[44px] sm:min-h-0"
              data-testid={`contact-phone-input-${biz.id}`}
            />
          </label>
          <label className="block">
            <span className="text-[10px] text-muted-foreground block mb-0.5">Email</span>
            <input
              type="email"
              inputMode="email"
              autoComplete="email"
              autoCapitalize="off"
              value={contactEmail}
              onChange={(e) => setContactEmail(e.target.value.slice(0, 120))}
              onBlur={() => persistContact("contact_email", contactEmail.trim())}
              placeholder="contact@entreprise.fr"
              className="w-full text-base sm:text-xs font-mono border border-border rounded-sm px-2 py-2 sm:py-1.5 bg-background focus:border-primary outline-none min-h-[44px] sm:min-h-0"
              data-testid={`contact-email-input-${biz.id}`}
            />
          </label>
        </div>
        <p className="text-[10px] text-muted-foreground mt-1.5">
          💡 Trouve ces infos avec le bouton <b>🔍 Google</b> ci-dessus. Tout est sauvegardé : tu peux les recopier dans
          ton téléphone à la fin de la journée.
        </p>

        {contactEmail && (
          <div className="mt-2.5 flex items-center gap-2 flex-wrap" data-testid={`compose-email-block-${biz.id}`}>
            {telepro ? (
              <button
                type="button"
                onClick={() => setEmailPreviewOpen(true)}
                className="bg-primary text-primary-foreground hover:bg-[#0f3d29] hover:text-white px-3 py-1.5 rounded-sm text-xs font-bold inline-flex items-center gap-1.5"
                data-testid={`compose-email-btn-${biz.id}`}
                title="Ouvre la prévisualisation pour vérifier ou modifier le mail avant l'envoi"
              >
                <PaperPlaneTilt size={12} weight="bold"/> Prévisualiser & envoyer email
              </button>
            ) : (
              <span
                className="text-[11px] bg-secondary/10 text-secondary border border-secondary/30 rounded-sm px-2 py-1 inline-flex items-center gap-1"
                data-testid={`compose-email-blocked-${biz.id}`}
                title="Configure ton Gmail pro en haut du portail pour activer cette fonction"
              >
                ⚠️ Configure ton Gmail pro en haut du portail pour activer l&apos;email pré-rédigé
              </span>
            )}
          </div>
        )}
      </div>

      <div className="mb-3" data-testid={`assist-method-${biz.id}`}>
        <div className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground mb-1.5">
          Tu l&apos;as contacté par :
        </div>
        <div className="flex gap-1.5 flex-wrap">
          {[
            { id: "phone", label: "📞 Téléphone" },
            { id: "email", label: "📧 Email" },
            { id: "both", label: "📞 + 📧 Les deux" },
          ].map((m) => {
            const active = currentMethod === m.id;
            return (
              <button
                key={m.id}
                type="button"
                onClick={() => setMethod(m.id)}
                className={`text-[11px] font-semibold border rounded-sm px-2.5 py-1 transition ${
                  active
                    ? "bg-primary text-primary-foreground border-primary hover:bg-[#0f3d29] hover:text-white"
                    : "bg-card hover:bg-primary/10 text-foreground border-border"
                }`}
                data-testid={`method-${m.id}-${biz.id}`}
              >
                {m.label}{active && " ✓"}
              </button>
            );
          })}
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-[1fr_auto] gap-2 items-start">
        <div className="flex items-start gap-1.5">
          <textarea
            value={noteDraft}
            onChange={(e) => setNoteDraft(e.target.value.slice(0, 500))}
            onBlur={() => { if (noteDraft !== (biz.call.note || "")) onSaveNote(noteDraft); }}
            rows={2}
            placeholder="Note rapide (objection retenue, prénom décideur, créneau de rappel…)"
            className="w-full text-xs border border-border rounded-sm px-2 py-1.5 bg-background focus:border-primary outline-none"
            data-testid={`assist-note-${biz.id}`}
          />
          <MicButton
            testid={`assist-note-mic-${biz.id}`}
            onAppend={(chunk) => {
              const cleaned = chunk.trim();
              if (!cleaned) return;
              setNoteDraft((cur) => {
                const base = (cur || "").trim();
                const merged = base ? `${base} ${cleaned}` : cleaned;
                return merged.slice(0, 500);
              });
            }}
          />
        </div>
        <div className="flex flex-wrap gap-1.5 sm:flex-col sm:items-stretch">
          <button
            onClick={closeAfter(() => onSetStatus("sale"))}
            className="bg-primary text-primary-foreground hover:bg-[#0f3d29] hover:text-white px-3 py-1.5 rounded-sm text-xs font-bold inline-flex items-center justify-center gap-1.5"
            data-testid={`assist-set-sale-${biz.id}`}
          >
            <Trophy size={12} weight="fill"/> Vente !
          </button>
          <button
            onClick={closeAfter(() => onSetStatus("site_web_besoin"))}
            className="bg-[#1a5f8a]/15 text-[#1a5f8a] border border-[#1a5f8a]/40 hover:bg-[#1a5f8a]/25 px-3 py-1.5 rounded-sm text-xs font-bold inline-flex items-center justify-center gap-1.5"
            data-testid={`assist-set-site-web-${biz.id}`}
            title="Le prospect est intéressé par un site web via Digital Corp France"
          >
            <GlobeHemisphereWest size={12} weight="bold"/> Besoin site web
          </button>
          <button
            onClick={closeAfter(() => onSetStatus("callback"))}
            className="bg-[#C8795A]/15 text-[#C8795A] border border-[#C8795A]/40 hover:bg-[#C8795A]/25 px-3 py-1.5 rounded-sm text-xs font-bold inline-flex items-center justify-center gap-1.5"
            data-testid={`assist-set-callback-${biz.id}`}
          >
            <Clock size={12} weight="bold"/> Rappeler
          </button>
          <button
            onClick={closeAfter(() => onSetStatus("no_answer"))}
            className="text-xs text-muted-foreground hover:text-foreground bg-card border border-border rounded-sm px-3 py-1.5 font-semibold"
            data-testid={`assist-set-no-answer-${biz.id}`}
          >
            Pas joint
          </button>
        </div>
      </div>

      {emailPreviewOpen && (
        <EmailPreviewModal
          biz={biz}
          telepro={telepro}
          contactName={contactName}
          contactEmail={contactEmail}
          onClose={() => setEmailPreviewOpen(false)}
          onSent={() => {
            if (!biz.call.contact_method) onSaveContact({ contact_method: "email" });
            setEmailPreviewOpen(false);
          }}
        />
      )}
    </div>
  );
}
